<template>
  <div
    class="order"
    :class="{ unsaved: Object.keys(order._changes).length > 0 }"
  >
    <div>
      <div class="listTop">
        <div class="listTopLeft">
          <div class="orderId">
            <a
              class="orderLink"
              :href="`/zgloszenie/${order.id}`"
              target="_blank"
            >
              <font-awesome-icon icon="link"/>
              {{ order.id }}
            </a>
              <a
                  class="orderLink"
                  :href="`/api/order/${order.id}/pdf`"
                  target="_blank"
              >
                  PDF
              </a>
          </div>
          <div class="orderCreatedAt">
            <span>{{ getDateTime(order.created_at) }}</span>
          </div>
          <div class="orderStatusWrapper">
            <button
              type="button"
              class="orderStatus"
              :class="order?._changes?.status ?? order.status"
            >
              {{ getStatus(order._changes.hasOwnProperty('status') ? order._changes.status : order.status) }}
            </button>
          </div>
        </div>
        <div class="listTopMiddle">
          <div>
            <div class="orderCompanyName">
              {{ order.company_name }}
            </div>
            <div class="orderTaxNumber">
              {{ $t('orderList.table.taxNumber') }}:
              {{ order.tax_number }}
            </div>
          </div>
        </div>
        <div class="listTopRight">
          <div class="orderSubmitTop">
            <div v-if="saveAvailable()">
              <TextIconButton
                :click="handleSave"
                :label="$t('orderList.save')"
                :icon="'floppy-disk'"
                :disabled="order._saveSubmitted || order._closeSubmitted"
                :spinner="order._saveSubmitted"
              />
            </div>
            <div v-if="closeAvailable()">
              <TextIconButton
                :click="handleClose"
                :label="$t('orderList.close')"
                :icon="'paper-plane'"
                :colorGreen="true"
                :disabled="order._saveSubmitted || order._closeSubmitted"
                :spinner="order._closeSubmitted"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        :class="{ sidebarExpanded: $root.sidebarExpanded }"
        class="listBottom"
      >
        <div
          class="orderFinalOpinionSummary"
          :class="order._changes.hasOwnProperty('final_opinion') ? order._changes.final_opinion : order.final_opinion"
        >
          <em>{{ $t('orderList.table.finalOpinionSummary') }}</em>
          <p>{{ getFinalOpinion() }}</p>
        </div>
        <div class="orderFinishedAt">
          <em>{{ $t('orderList.table.finishedAt') }}</em>
          {{ getDateTime(order.finished_at) ?? '-' }}
        </div>
        <div class="orderSLA">
          <em>{{ $t('orderList.table.SLA') }}</em>
          <OrderSLA
            :createdAt="parseDate(order.created_at)"
            :finishedAt="parseDate(order.finished_at)"
          />
        </div>
        <div class="orderClientName">
          <em>{{ $t('orderList.table.clientName') }}</em>
          {{ getClientName() }}
        </div>
        <div class="orderAssignedUserNameWrapper">
          <em>{{ $t('orderList.table.assignedUserName') }}</em>
          <button
            type="button"
            class="orderAssignedUserName"
            @click.prevent.stop="handleAssignedUserDropdownClick()"
            :class="{ expanded: order._expandedAssignedUserDropdown }"
            :disabled="!isEnabled() || order._assignedUserSubmitted"
          >
            <span v-if="!order._assignedUserSubmitted">
              {{ getAssignedUserName() }}
              <font-awesome-icon icon="chevron-down"/>
            </span>
            <span
              v-if="order._assignedUserSubmitted"
              class="spinner"
            >
              <font-awesome-icon icon="rotate"/>
            </span>
          </button>
          <ul
            class="orderAssignedUserNameDropdown"
            :class="{ expanded: order._expandedAssignedUserDropdown }"
          >
            <li
              v-for="user in abgActiveUsers"
              @click.prevent.stop="handleAssignedUserChange(user)"
              :key="user.id"
            >
              {{ user.name }}
            </li>
          </ul>
        </div>
        <div
          v-if="!fullView"
          :class="{ sidebarExpanded: $root.sidebarExpanded }"
          class="orderMoreLessButton"
        >
          <MoreLessButton
            :click="() => order._expanded = !order._expanded"
            :labelLess="$t('orderList.less')"
            :labelMore="$t('orderList.more')"
            :expanded="order._expanded"
          />
        </div>
      </div>
    </div>
    <div
      class="additionalInfo"
      :class="{ expanded: order._expanded || fullView }"
    >
      <div class="categoryWrapper">
        <h2 class="category">{{ $t('orderList.subHeadings.orderData') }}</h2>
      </div>
      <div class="flex3">
        <div>
          <h3 class="subCategory">{{ $t('orderList.subHeadings.principal') }}</h3>
          <OrderInfo
            :label="$t('orderList.table.principalContactPerson')"
            :value="getValue('principal_contact_person') ?? '-'"
          />
          <OrderInfo
            :label="$t('orderList.table.principalPhoneNumber')"
            :value="getValue('principal_phone_number') ?? '-'"
          />
          <OrderInfo
            :label="$t('orderList.table.principalEmailAddress')"
            :value="getValue('principal_email_address') ?? '-'"
          />
        </div>
        <div>
          <h3 class="subCategory">{{ $t('orderList.subHeadings.customer') }}</h3>
          <OrderInfo
            :label="$t('orderList.table.customerContactPerson')"
            :value="getValue('customer_contact_person') ?? '-'"
          />
          <OrderInfo
            :label="$t('orderList.table.customerPhoneNumber')"
            :value="getValue('customer_phone_number') ?? '-'"
          />
          <OrderInfo
            :label="$t('orderList.table.customerEmailAddress')"
            :value="getValue('customer_email_address') ?? '-'"
          />
          <OrderInfo
            :label="$t('orderList.table.customerAddress')"
            :value="getValue('customer_address') ?? '-'"
          />
        </div>
        <div>
          <h3 class="subCategory">{{ $t('orderList.subHeadings.subjectOfFinancing') }}</h3>
          <OrderInfo
            :label="$t('orderList.table.subjectOfFinancing')"
            :value="getValue('subject_of_financing') ?? '-'"
          />
          <OrderInfo
            :label="$t('orderList.table.offerValue')"
            :value="getOfferValue('offer_value')"
          />
          <OrderInfo
            :label="$t('orderList.table.customerType')"
            :value="getValue('customer_type') ?? '-'"
          />
          <OrderInfo
            :label="$t('orderList.table.transactionType')"
            :value="getValue('transaction_type') ?? '-'"
          />
        </div>
      </div>
      <div class="categoryWrapper">
        <h2 class="category">{{ $t('orderList.subHeadings.verificationInSources') }}</h2>
      </div>
      <div class="flex2">
        <OrderTextarea
          :label="$t('orderList.table.verificationInAvailableDatabases')"
          :disabled="!isEnabled()"
          :value="getValue('verification_in_available_databases')"
          :input="event => handleChange(event, 'verification_in_available_databases')"
          :blur="() => handleBlur('verification_in_available_databases')"
          :error="getError('verification_in_available_databases')"
          :rows="isEnabled() ? 2 : 1"
        />
        <OrderTextarea
          :label="$t('orderList.table.verificationInOtherSources')"
          :disabled="!isEnabled()"
          :value="getValue('verification_in_other_sources')"
          :input="event => handleChange(event, 'verification_in_other_sources')"
          :blur="() => handleBlur('verification_in_other_sources')"
          :error="getError('verification_in_other_sources')"
          :rows="isEnabled() ? 2 : 1"
        />
      </div>
      <div class="categoryWrapper">
        <h2 class="category">{{ $t('orderList.subHeadings.findingsMade') }}</h2>
      </div>
      <div class="categoryWrapper">
        <h3 class="subCategory">{{ $t('orderList.subHeadings.contact') }}</h3>
      </div>
      <div class="flex3">
        <OrderTextarea
          :label="$t('orderList.table.contactPerson')"
          :disabled="!isEnabled()"
          :value="getValue('contact_person')"
          :input="event => handleChange(event, 'contact_person')"
          :blur="() => handleBlur('contact_person')"
          :error="getError('contact_person')"
          :rows="isEnabled() ? 2 : 1"
        />
        <OrderTextarea
          :label="$t('orderList.table.phoneNumber')"
          :disabled="!isEnabled()"
          :value="getValue('phone_number')"
          :input="event => handleChange(event, 'phone_number')"
          :blur="() => handleBlur('phone_number')"
          :error="getError('phone_number')"
          :rows="isEnabled() ? 2 : 1"
        />
        <OrderTextarea
          :label="$t('orderList.table.emailAddress')"
          :disabled="!isEnabled()"
          :value="getValue('email_address')"
          :input="event => handleChange(event, 'email_address')"
          :blur="() => handleBlur('email_address')"
          :error="getError('email_address')"
          :rows="isEnabled() ? 2 : 1"
        />
      </div>
      <div class="categoryWrapper">
        <h3 class="subCategory">{{ $t('orderList.subHeadings.location') }}</h3>
      </div>
      <div class="flex3">
        <OrderTextarea
          :label="$t('orderList.table.headquarterAddress')"
          :disabled="!isEnabled()"
          :value="getValue('headquarter_address')"
          :input="event => handleChange(event, 'headquarter_address')"
          :blur="() => handleBlur('headquarter_address')"
          :error="getError('headquarter_address')"
          :rows="isEnabled() ? 2 : 1"
        />
        <OrderTextarea
          :label="$t('orderList.table.headquarterDescription')"
          :disabled="!isEnabled()"
          :value="getValue('headquarter_description')"
          :input="event => handleChange(event, 'headquarter_description')"
          :blur="() => handleBlur('headquarter_description')"
          :error="getError('headquarter_description')"
          :rows="isEnabled() ? 2 : 1"
        />
        <OrderTextarea
          :label="$t('orderList.table.subjectOfLeaseLocation')"
          :disabled="!isEnabled()"
          :value="getValue('subject_of_lease_location')"
          :input="event => handleChange(event, 'subject_of_lease_location')"
          :blur="() => handleBlur('subject_of_lease_location')"
          :error="getError('subject_of_lease_location')"
          :rows="isEnabled() ? 2 : 1"
        />
      </div>
      <div class="categoryWrapper">
        <h3 class="subCategory">{{ $t('orderList.subHeadings.financing') }}</h3>
      </div>
      <div class="flex3">
        <OrderTextarea
          :label="$t('orderList.table.purposeAndLegitimacy')"
          :disabled="!isEnabled()"
          :value="getValue('purpose_and_legitimacy')"
          :input="event => handleChange(event, 'purpose_and_legitimacy')"
          :blur="() => handleBlur('purpose_and_legitimacy')"
          :error="getError('purpose_and_legitimacy')"
          :rows="isEnabled() ? 2 : 1"
        />
        <OrderTextarea
          :label="$t('orderList.table.financialSupport')"
          :disabled="!isEnabled()"
          :value="getValue('financial_support')"
          :input="event => handleChange(event, 'financial_support')"
          :blur="() => handleBlur('financial_support')"
          :error="getError('financial_support')"
          :rows="isEnabled() ? 2 : 1"
        />
        <OrderTextarea
          :label="$t('orderList.table.supplierConnections')"
          :disabled="!isEnabled()"
          :value="getValue('supplier_connections')"
          :input="event => handleChange(event, 'supplier_connections')"
          :blur="() => handleBlur('supplier_connections')"
          :error="getError('supplier_connections')"
          :rows="isEnabled() ? 2 : 1"
        />
      </div>
      <div class="categoryWrapper">
        <h3 class="subCategory">{{ $t('orderList.subHeadings.other') }}</h3>
      </div>
      <div class="flex2">
        <OrderTextarea
          :label="$t('orderList.table.fieldVision')"
          :disabled="!isEnabled()"
          :value="getValue('field_vision')"
          :input="event => handleChange(event, 'field_vision')"
          :blur="() => handleBlur('field_vision')"
          :error="getError('field_vision')"
          :rows="isEnabled() ? 2 : 1"
        />
        <OrderTextarea
          :label="$t('orderList.table.placeOfActivityAssessment')"
          :disabled="!isEnabled()"
          :value="getValue('place_of_activity_assessment')"
          :input="event => handleChange(event, 'place_of_activity_assessment')"
          :blur="() => handleBlur('place_of_activity_assessment')"
          :error="getError('place_of_activity_assessment')"
          :rows="isEnabled() ? 2 : 1"
        />
        <OrderTextarea
          :label="$t('orderList.table.subjectOfLeaseAssessment')"
          :disabled="!isEnabled()"
          :value="getValue('subject_of_lease_assessment')"
          :input="event => handleChange(event, 'subject_of_lease_assessment')"
          :blur="() => handleBlur('subject_of_lease_assessment')"
          :error="getError('subject_of_lease_assessment')"
          :rows="isEnabled() ? 2 : 1"
        />
        <OrderTextarea
          v-if="['abg_manager', 'abg_user', 'client_manager'].includes(user.role)"
          :label="$t('orderList.table.customerInformation')"
          :disabled="!isEnabled()"
          :value="getValue('customer_information')"
          :input="event => handleChange(event, 'customer_information')"
          :blur="() => handleBlur('customer_information')"
          :error="getError('customer_information')"
          :rows="isEnabled() ? 2 : 1"
        />
      </div>
      <div
        v-if="isEnabled() ||  order.attachments.length > 0"
        class="categoryWrapper"
      >
        <h2 class="category">{{ $t('orderList.subHeadings.attachments') }}</h2>
      </div>
      <OrderAttachments
        :disabled="!isEnabled()"
        :order="order"
      />
      <div class="categoryWrapper">
        <h2 class="category">{{ $t('orderList.subHeadings.finalOpinion') }}</h2>
      </div>
      <div>
        <OrderFinalOpinion
          :disabled="!isEnabled()"
          :value="getValue('final_opinion')"
          :click="finalOpinion => handleFinalOpinionChange(finalOpinion, 'final_opinion')"
          :error="getError('final_opinion')"
        />
      </div>
      <div class="flex2">
        <OrderTextarea
          :label="$t('orderList.table.finalOpinionDescription')"
          :disabled="!isEnabled()"
          :value="getValue('final_opinion_description')"
          :input="event => handleChange(event, 'final_opinion_description')"
          :blur="() => handleBlur('final_opinion_description')"
          :error="getError('final_opinion_description')"
          :rows="isEnabled() ? 2 : 1"
        />
        <OrderTextarea
          v-if="['abg_manager', 'abg_user'].includes(user.role)"
          :label="$t('orderList.table.notes')"
          :disabled="!isEnabled()"
          :value="getValue('notes')"
          :input="event => handleChange(event, 'notes')"
          :blur="() => handleBlur('notes')"
          :error="getError('notes')"
          :rows="isEnabled() ? 2 : 1"
        />
      </div>
      <div
        v-if="
          (['client_manager', 'abg_manager'].includes(user.role) && ['awaiting_acceptance', 'accepted'].includes(order.status))
          || (['abg_manager', 'abg_user'].includes(user.role) && order.status === 'accepted')
        "
        class="categoryWrapper"
      >
        <h2 class="category">{{ $t('orderList.subHeadings.finalOpinionConfirmation') }}</h2>
      </div>
      <div
        v-if="
          (['client_manager', 'abg_manager'].includes(user.role) && ['awaiting_acceptance', 'accepted'].includes(order.status))
          || (['abg_manager', 'abg_user'].includes(user.role) && order.status === 'accepted')
        "
      >
        <OrderFinalOpinion
          :disabled="!(['client_manager', 'abg_manager'].includes(user.role) && order.status === 'awaiting_acceptance')"
          :value="getValue('final_opinion_confirmation')"
          :click="finalOpinion => handleFinalOpinionChange(finalOpinion, 'final_opinion_confirmation')"
          :error="getError('final_opinion_confirmation')"
        />
      </div>
      <div
        v-if="
          (['client_manager', 'abg_manager'].includes(user.role) && ['awaiting_acceptance', 'accepted'].includes(order.status))
          || (['abg_manager', 'abg_user'].includes(user.role) && order.status === 'accepted')
        "
        class="flex2"
      >
        <OrderTextarea
          :label="$t('orderList.table.finalOpinionConfirmationDescription')"
          :disabled="!(['client_manager', 'abg_manager'].includes(user.role) && order.status === 'awaiting_acceptance')"
          :value="getValue('final_opinion_confirmation_description')"
          :input="event => handleChange(event, 'final_opinion_confirmation_description')"
          :blur="() => handleBlur('final_opinion_confirmation_description')"
          :error="getError('final_opinion_confirmation_description')"
          :rows="isEnabled() ? 2 : 1"
        />
      </div>
      <div class="orderSubmitBottom">
        <div v-if="saveAvailable()">
          <TextIconButton
            :click="() => handleSave()"
            :label="$t('orderList.save')"
            :icon="'floppy-disk'"
            :disabled="order._saveSubmitted || order._closeSubmitted"
            :spinner="order._saveSubmitted"
            :sizeBig="true"
          />
        </div>
        <div v-if="closeAvailable()">
          <TextIconButton
            :click="() => handleClose()"
            :label="$t('orderList.close')"
            :icon="'paper-plane'"
            :disabled="order._saveSubmitted || order._closeSubmitted"
            :spinner="order._closeSubmitted"
            :colorGreen="true"
            :sizeBig="true"
          />
        </div>
        <div v-if="acceptAvailable()">
          <TextIconButton
            :click="() => handleAccept()"
            :label="$t('orderList.accept')"
            :icon="'paper-plane'"
            :disabled="order._saveSubmitted || order._closeSubmitted"
            :spinner="order._closeSubmitted"
            :colorGreen="true"
            :sizeBig="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .order {
    font-size: 1.3rem;
    background-color: var(--secondary-background-color);
    border: 2px solid #e9e9e9;
    border-radius: 5px;
    box-shadow: 6px 6px 22px -15px rgba(130, 130, 130, 1);
  }

  .orderLink {
    color: var(--tertiary-color);
    background-color: transparent;
    text-decoration: none;
    transition-property: color;
    transition-duration: 0.2s;
  }

  .orderLink:hover {
    color: var(--secondary-color);
  }

  .order.unsaved {
    border-color: var(--primary-color);
  }

  .additionalInfo {
    height: 0;
    padding: 0;
    overflow: hidden;
    transition-property: padding;
    transition-duration: 0.2s;
  }

  .additionalInfo.expanded {
    border-top: 1px solid #e9e9e9;
    height: auto;
    padding: 20px;
  }

  .flex2 {
    display: flex;
    flex-wrap: wrap;
  }

  .flex2 > div {
    flex: 1 0 350px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 20px;
  }

  .flex3 {
    display: flex;
    flex-wrap: wrap;
  }

  .flex3 > div {
    flex: 1 0 250px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 20px;
  }

  .listTop {
    display: flex;
    flex-wrap: wrap;
  }

  .listTopLeft {
    flex: 0 0 250px;
    display: flex;
    flex-wrap: wrap;
    box-sizing: border-box;
    padding: 20px;
  }

  .listTopMiddle {
    flex: 1 0 300px;
    box-sizing: border-box;
    padding: 20px;
  }

  .listTopRight {
    flex: 0 0 135px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    margin-left: auto;
    padding: 20px;
  }

  .listBottom {
    display: flex;
    flex-wrap: wrap;
    margin: 0 20px;
    border-top: 1px solid #e9e9e9;
  }

  .listBottom > div:not(:last-child) {
    margin: 20px 20px 20px 0;
  }

  .listBottom > div:last-child {
    margin: 20px 0 20px auto;
  }

  .listBottom > div:nth-child(1) {
    flex: 0 0 105px;
  }

  .listBottom > div:nth-child(2) {
    flex: 0 0 115px;
  }

  .listBottom > div:nth-child(3) {
    flex: 0 0 70px;
  }

  .listBottom > div:nth-child(4) {
    flex: 0 0 120px;
  }

  .listBottom > div:nth-child(5) {
    flex: 1 0 140px;
  }

  .listBottom > div:nth-child(6) {
    flex: 0 0 80px;
  }

  .orderId {
    flex: 0 0 40%;
  }

  .orderCreatedAt {
    flex: 0 0 60%;
    text-align: right;
  }

  .orderCreatedAt > span {
    display: block;
  }

  .orderStatusWrapper {
    flex: 0 0 100%;
    position: relative;
    margin: auto 0;
  }

  .orderStatus {
    position: relative;
    box-sizing: border-box;
    width: 100%;
    margin-top: 10px;
    padding: 5px;
    font-weight: 600;
    text-transform: uppercase;
    text-align: center;
    color: var(--primary-text-color-white);
    border: none;
    border-radius: 3px;
    transition: background-color 0.2s;
  }

  .orderStatus.new {
    background-color: #35c589;
  }

  .orderStatus.in_progress {
    background-color: #02c3ed;
  }

  .orderStatus.awaiting_acceptance {
    background-color: #4F5D75;
  }

  .orderStatus.closed,
  .orderStatus.accepted {
    background-color: #c3c3c3;
  }

  .orderCompanyName {
    font-size: 1.8rem;
    font-weight: 600;
    overflow-wrap: break-word;
  }

  .orderTaxNumber {
    margin-top: 10px;
  }

  .orderFinalOpinionSummary {
    position: relative;
  }

  .orderFinalOpinionSummary > em {
    display: block;
    font-weight: 600;
    font-style: normal;
    color: var(--secondary-text-color);
  }

  .orderFinalOpinionSummary > p {
    margin: 0;
    padding: 0;
    text-align: left;
    text-transform: uppercase;
  }

  .orderFinalOpinionSummary.positive > p {
    font-weight: 600;
    color: #35c589;
  }

  .orderFinalOpinionSummary.negative > p {
    font-weight: 600;
    color: var(--error-color);
  }

  .orderAssignedUserNameWrapper {
    position: relative;
  }

  .orderAssignedUserNameWrapper > em {
    display: block;
    font-style: normal;
    font-weight: 600;
    color: var(--secondary-text-color);
  }

  .orderAssignedUserName {
    display: block;
    width: 100%;
    padding: 0;
    text-align: left;
    background-color: transparent;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    transition-property: background-color;
    transition-duration: 0.2s;
  }

  .orderAssignedUserName:hover {
    background-color: #f3f3f3;
  }

  .orderAssignedUserName:disabled {
    color: initial;
    cursor: text;
  }

  .orderAssignedUserName:disabled:hover {
    background-color: transparent;
  }

  .orderAssignedUserName > .label > svg {
    padding-bottom: 2px;
    font-size: 1rem;
    color: var(--secondary-text-color);
    transition: transform 0.2s;
  }

  .orderAssignedUserName:disabled > .label > svg {
    opacity: 0;
  }

  .orderAssignedUserName.expanded > .label > svg {
    transform: rotate(180deg);
  }

  @keyframes rotateSpinner {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .orderAssignedUserName > .spinner > svg {
    display: inline-block;
    padding-bottom: 2px;
    font-size: 1rem;
    color: var(--secondary-text-color);
    animation: rotateSpinner 1.5s linear infinite;
    transform: rotate(0deg);
  }

  .orderAssignedUserNameDropdown {
    position: absolute;
    top: auto;
    left: auto;
    height: 0;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    text-align: center;
    color: var(--primary-text-color-white);
    background-color: var(--tertiary-color);
    border-radius: 3px;
    list-style-type: none;
    cursor: pointer;
    overflow: hidden;
    transition-property: height, padding;
    transition-duration: 0.2s;
    z-index: 10;
  }

  .orderAssignedUserNameDropdown.expanded {
    height: auto;
    padding: 10px 5px;
  }

  .orderAssignedUserNameDropdown > li {
    padding: 5px;
    background-color: transparent;
    transition: background-color 0.2s;
  }

  .orderAssignedUserNameDropdown > li:hover {
    background-color: #535d83;
  }

  .orderAssignedUserNameDropdown > li:not(:last-child) {
    border-bottom: 1px solid #3e3e3e;
  }

  .orderClientName > em {
    display: block;
    font-style: normal;
    font-weight: 600;
    color: var(--secondary-text-color);
  }

  .orderFinishedAt > em {
    display: block;
    font-style: normal;
    font-weight: 600;
    color: var(--secondary-text-color);
  }

  .orderSLA > em {
    display: block;
    font-style: normal;
    font-weight: 600;
    color: var(--secondary-text-color);
  }

  .orderMoreLessButton {
    margin: auto 0 0 auto;
    text-align: right;
  }

  .orderSubmitTop {
    text-align: center;
  }

  .orderSubmitTop > div:nth-child(2) {
    margin-top: 10px;
  }

  .orderSubmitBottom {
    flex: 0 0 100%;
    padding: 20px;
    text-align: center;
  }

  .orderSubmitBottom > div {
    display: inline-block;
    margin: 20px;
  }

  @media only screen and (max-width: 770px) {
    .listTop.sidebarExpanded > .listTopLeft,
    .listTop.sidebarExpanded > .listTopRight {
      flex: 100%;
    }
  }

  @media only screen and (max-width: 550px) {
    .listTop > .listTopLeft,
    .listTop > .listTopRight {
      flex: 100%;
    }
  }

  @media only screen and (max-width: 770px) {
    .listBottom.sidebarExpanded > div:nth-child(6) {
      flex: 100%;
    }

    .orderMoreLessButton.sidebarExpanded {
      text-align: center;
    }
  }

  @media only screen and (max-width: 550px) {
    .listBottom > div:nth-child(6) {
      flex: 100%;
    }

    .orderMoreLessButton {
      text-align: center;
    }
  }
</style>

<script>
  import { validateSingleField, validateAllFields } from '../pages/orderValidation.js';
  import OrderInput from './OrderInput.vue';
  import OrderTextarea from './OrderTextarea.vue';
  import OrderInfo from './OrderInfo.vue';
  import MoreLessButton from '../buttons/MoreLessButton.vue';
  import TextIconButton from '../buttons/TextIconButton.vue';
  import OrderSLA from './OrderSLA.vue';
  import OrderFinalOpinion from '../order/OrderFinalOpinion';
  import OrderAttachments from '../order/OrderAttachments';

  export default {
    components: {
      OrderInput,
      OrderTextarea,
      OrderInfo,
      MoreLessButton,
      TextIconButton,
      OrderSLA,
      OrderFinalOpinion,
      OrderAttachments
    },
    props: [ 'order', 'user', 'clients', 'users', 'fullView' ],
    computed: {
      abgActiveUsers() {
        return this.users.filter(user => {
          return ['abg_manager', 'abg_user'].includes(user.role)
            && user.is_active === 1;
        });
      }
    },
    methods: {
      handleFinalOpinionChange(finalOpinion, key) {
        if (this.getValue(key) === finalOpinion) {
          if (this.order[key] === null) {
            delete this.order._changes[key];
          } else {
            this.order._changes[key] = null;
          }
        } else if (this.order[key] === finalOpinion) {
          delete this.order._changes[key];
        } else {
          this.order._changes[key] = finalOpinion;
        }
      },
      handleAssignedUserDropdownClick() {
        this.$emit('resetExpansions');
        this.order._expandedAssignedUserDropdown = !this.order._expandedAssignedUserDropdown;
      },
      getOfferValue() {
        const value = this.getValue('offer_value') / 100;

        return value.toLocaleString([], { style: 'currency', currency: 'PLN' });
      },
      getValue(key) {
        if (this.order._changes.hasOwnProperty(key)) {
          return this.order._changes[key];
        }

        return this.order[key];
      },
      getError(key) {
        if (this.order._errors.hasOwnProperty(key)) {
          return this.order._errors[key];
        }

        return '';
      },
      handleChange(event, key) {
        if (this.order[key] === event.target.value) {
          delete this.order._changes[key];
        } else {
          this.order._changes[key] = event.target.value;
        }

        const currentError = this.getError(key);

        if (currentError.length > 0) {
          const message = validateSingleField(this.order, key);

          if (message.length > 0) {
            this.order._errors[key] = message;
          } else {
            delete this.order._errors[key];
          }
        }
      },
      handleBlur(key) {
        const message = validateSingleField(this.order, key);

        if (message.length > 0) {
          const currentError = this.getError(key);

          if (currentError !== message) {
            this.order._errors[key] = message;
          }
        } else {
          delete this.order._errors[key];
        }

        this.$forceUpdate();
      },
      isEnabled() {
        if (this.user.role === 'abg_user') {
          return ['new', 'in_progress'].includes(this.order.status) && (this.order.assigned_user_id === parseInt(this.user.id) || this.order.assigned_user_id === null);
        } else if (this.user.role === 'abg_manager') {
          return ['new', 'in_progress'].includes(this.order.status);
        }

        return false;
      },
      saveAvailable() {
        if (Object.keys(this.order._changes).length > 0) {
          return ['new', 'in_progress'].includes(this.order.status);;
        }

        return false;
      },
      closeAvailable() {
        if (this.order.final_opinion?.length > 0 || this.order._changes.hasOwnProperty('final_opinion')) {
          return ['new', 'in_progress'].includes(this.order.status);
        }

        return false;
      },
      acceptAvailable() {
        if (this.order.final_opinion_confirmation?.length > 0 || this.order._changes.hasOwnProperty('final_opinion_confirmation')) {
          return ['awaiting_acceptance'].includes(this.order.status);
        }

        return false;
      },
      validateForm() {
        const errors = validateAllFields(this.order);
        this.order._errors = errors;

        if (Object.keys(errors).length > 0) {
          return false;
        }

        return true;
      },
      updateOrderData: async function (order) {
        const response = await fetch(`/api/order/${order.id}`, {
          method: 'PUT',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(order)
        });

        const responseJson = await response.json();
        const updatedOrder = {
          ...responseJson,
          _changes: {},
          _errors: {},
          _index: order._index,
          _expanded: false,
          _clientName: this.clients.find(client => client.id === responseJson.client_id)?.name ?? '',
          _assignedUserName: this.users.find(user => user.id === responseJson.assigned_user_id)?.name ?? ''
        };
        updatedOrder._expanded = order._expanded;
        return updatedOrder;
      },
      handleAssignedUserChange: async function(user) {
        this.order._assignedUserSubmitted = true;
        this.order._expandedAssignedUserDropdown = false;
        this.$emit('updateOrder', this.order);

        const data = {
          _index: this.order._index,
          id: this.order.id,
          assigned_user_id: user.id,
          _changes: {...this.order._changes},
          _expanded: this.order._expanded
        };

        if (this.order.status === 'new') {
          data.status = 'in_progress';
        }

        const response = await this.updateOrderData(data);
        response._changes = data._changes;
        this.order._assignedUserSubmitted = false;
        this.$emit('updateOrder', response);
      },
      handleSave: async function () {
        this.order._saveSubmitted = true;

        const combinedData = {
          ...this.order,
          ...this.order._changes
        };

        if (!combinedData.assigned_user_id) {
          combinedData.assigned_user_id = this.user.id;
        }

        if (combinedData.status === 'new') {
          combinedData.status = 'in_progress';
        }

        const response = await this.updateOrderData(combinedData);
        this.order._saveSubmitted = false;
        this.$emit('updateOrder', response);
      },
      handleClose: async function () {
        this.order._closeSubmitted = true;
        const validation = this.validateForm(this.order);

        if (!validation) {
          this.order._closeSubmitted = false;
          return;
        }

        const combinedData = { ...this.order, ...this.order._changes };

        if (!combinedData.assigned_user_id) {
          combinedData.assigned_user_id = this.user.id;
        }

        if (['new', 'in_progress'].includes(combinedData.status)) {
          switch (combinedData.final_opinion) {
            case 'positive':
              combinedData.status = 'closed';
              break;
            case 'negative':
              combinedData.status = 'awaiting_acceptance';
              break;
          }
        }

        const response = await this.updateOrderData(combinedData);
        this.order._closeSubmitted = false;
        this.$emit('updateOrder', response);
      },
      handleAccept: async function () {
        this.order._closeSubmitted = true;
        const validation = this.validateForm(this.order);

        if (!validation) {
          this.order._closeSubmitted = false;
          return;
        }

        const combinedData = { ...this.order, ...this.order._changes };

        if (combinedData.status === 'awaiting_acceptance') {
          combinedData.status = 'accepted';
        }

        const response = await this.updateOrderData(combinedData);
        this.order._closeSubmitted = false;
        this.$emit('updateOrder', response);
      },
      parseDate(dateTime) {
        if (typeof dateTime !== 'string') {
          return null;
        }

        const timestamp = Date.parse(dateTime);

        if (isNaN(timestamp)) {
          return null;
        }

        return new Date(timestamp);
      },
      getDate(dateTime) {
        const parsed = this.parseDate(dateTime);

        if (parsed === null) {
          return null;
        }

        return `${parsed.getDate().toString().padStart(2, '0')}.${(parsed.getMonth() + 1).toString().padStart(2, '0')}.${parsed.getFullYear()}`;
      },
      getTime(dateTime) {
        const parsed = this.parseDate(dateTime);

        if (parsed === null) {
          return null;
        }

        return `${(parsed.getHours()).toString().padStart(2, '0')}:${parsed.getMinutes().toString().padStart(2, '0')}`;
      },
      getDateTime(dateTime) {
        const date = this.getDate(dateTime);
        const time = this.getTime(dateTime);

        if (!date || !time) {
          return null;
        }

        return `${date} ${time}`;
      },
      getStatus(status) {
        return this.$t(`orderList.status.${status}`);
      },
      getAssignedUserName() {
        if (this.order._changes.hasOwnProperty('assigned_user_id')) {
          return this.order._changes._assignedUserName;
        }

        if (this.order.assigned_user_id) {
          return this.order._assignedUserName;
        }

        return this.$t('orderList.assignedUserName.null');
      },
      getFinalOpinion() {
        const value = this.getValue('final_opinion');

        if (!value) {
          return '-';
        }

        return this.$t(`orderList.finalOpinion.${this.getValue('final_opinion')}`);
      },
      getClientName() {
        return this.order._clientName;
      }
    }
  }
</script>
